"use client";

import React from "react";
import { Wrench, ShieldCheck, Cpu, CircleCheck as CheckCircle2, ArrowRight, Layers, FileCheck2, Gauge } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const steps = [{
  step: "01",
  title: "Physical & Mechanical Installation",
  tagline: "Rigid mountings & optical alignment",
  description: "Full mechanical positioning, vibration-isolated mounting, optical bench alignment, and cleanroom-compliant physical integration.",
  icon: Wrench,
  deliverables: ["Vibration and leveling sign-off", "Process flange and optic docking", "Hazardous zone (ATEX/IECEx) compliance"]
}, {
  step: "02",
  title: "Utility & Environmental Verification",
  tagline: "Power, purge gas & ambient stability",
  description: "Precise confirmation of pneumatic supplies, purge gas line integrity, clean dry air (CDA), thermal stability, and power filtering.",
  icon: Gauge,
  deliverables: ["Purge pressure and flow confirmation", "UPS and power conditioner checkout", "Ambient temperature and humidity logs"]
}, {
  step: "03",
  title: "PLC/DCS & SCADA Interfacing",
  tagline: "Deterministic automation integration",
  description: "Hardwired I/O and industrial Ethernet bus setup connecting process analyzers directly to DeltaV, Siemens, Rockwell, or batch systems.",
  icon: Cpu,
  deliverables: ["OPC UA, Modbus TCP and Profinet testing", "Real-time spectral data streaming", "21 CFR Part 11 handshake protocol"]
}, {
  step: "04",
  title: "Commissioning & Operational Handover",
  tagline: "IQ/OQ verification & team enablement",
  description: "Comprehensive qualification documentation, baseline performance testing, standards validation, and complete operator training.",
  icon: ShieldCheck,
  deliverables: ["Executed IQ/OQ qualification protocols", "System acceptance test (SAT) certificate", "SOP documentation and staff handover"]
}];
export default function InstallationProcess() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : i * 0.12,
        ease: "easeOut"
      }
    })
  };
  return <section id="installationprocess" data-nav="light" className="bg-background py-20 lg:py-28 border-y border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Lifecycle Engineering
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Structured 4-Stage Installation &amp; Integration
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From optical docking to automated batch system sign-off, our certified field engineers follow strict protocols built for regulated pharma, biopharma, and continuous manufacturing facilities.
          </p>
        </div>

        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {steps.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.step} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-40px"
          }} variants={cardVariants} className="h-full flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card/60 shadow-xs hover:shadow-md transition-shadow duration-300 rounded-xl relative overflow-hidden group">
                  {/* Step indicator top bar */}
                  <div className="absolute top-0 inset-x-0 h-1 bg-muted group-hover:bg-primary transition-colors duration-300" />

                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground border border-border/60">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <span className="font-mono text-2xl font-bold text-muted-foreground/40 group-hover:text-primary/70 transition-colors">
                          {item.step}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-xs font-medium text-primary mt-1">
                        {item.tagline}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                        {item.description}
                      </p>

                      <div className="space-y-2.5 pt-4 border-t border-border/70">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-card-foreground">
                          <FileCheck2 className="size-3.5 text-primary" />
                          <span>Key Deliverables</span>
                        </div>
                        <ul className="space-y-2">
                          {item.deliverables.map((deliv, dIdx) => <li key={dIdx} className="flex items-start gap-2 text-xs text-muted-foreground">
                              <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="leading-tight">{deliv}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-4 mt-auto">
                    <div className="h-0.5 w-full bg-border/50 rounded-full group-hover:bg-primary/30 transition-colors" />
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom Callout Banner */}
        <div className="mt-12 lg:mt-16 bg-secondary rounded-xl p-6 sm:p-8 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="size-10 rounded-lg bg-background flex items-center justify-center border border-border shrink-0 mt-0.5">
              <Layers className="size-5 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-secondary-foreground">
                Need turnkey IQ/OQ validation protocols for your site?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Our specialists provide pre-installation site surveys, utility readiness checklists, and full qualification support.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="default" className="w-full sm:w-auto">
              <Link to="/QualificationLifecycleServicesPage">
                View Qualification Services
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
              <Link to="/DiscussYourApplicationPage">
                Discuss Project
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}